import { createBackgroundReq, getUpdatedDetailsReq } from "../../../redux/specialist-slices/auth.slice";
import { Box, Button, Checkbox, FormControlLabel, Icon, Link, Stack, Typography } from "@mui/material";
import InputField1 from "../../../components/ui/InputField1";
import FileInput from "../../../components/ui/FileInput";
import { useDispatch, useSelector } from "react-redux";
import { Link as NavLink } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import Icons from "../../../assets/js/Icons";
import { red } from "@mui/material/colors";
import moment from "moment";
import { uid } from "uid";

let persistedFormData = null;
let persistedFiles = null;

function Details({ handleChange }) {
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const { specialist } = useSelector((state) => state.specialistSlice);

  const getInitialFiles = () => {
    if (persistedFiles) {
      return persistedFiles;
    }
    return {
      backgroundCheck: null,
      privacyPolicyCheck: null,
      personalIdentification:
        specialist?.background?.personalIdentification || [],
      professionalIdentification:
        specialist?.background?.professionalIdentification || [],
    };
  };

  const files = useRef(getInitialFiles());

  const formRef = useRef(null);

  const handleBack = () => {
    if (formRef.current) {
      const formData = new FormData(formRef.current);

      persistedFormData = {
        dob: formData.get("dob") || "",
        backgroundCheck: formData.get("backgroundCheck") === "on",
        privacyPolicyCheck: formData.get("privacyPolicyCheck") === "on",
      };

      persistedFiles = { ...files.current };
    }

    dispatch(getUpdatedDetailsReq()).then(() => {
      handleChange("expertise");
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = {};
    const form = new FormData(e.target);
    let formData = Object.fromEntries(form);
    formData = {
      ...files.current,
      ...formData,
    };
    formData.fullName = specialist?.specialist?.fullName;
    Object.keys(formData)?.forEach((field) => {
      const value = formData[field];
      if (Array.isArray(value)) {
        if (!value?.length) {
          err[field] = true;
        } else {
          delete err[field];
        }
      } else {
        if (typeof value === "string" && (value?.trim() === "" || !value)) {
          err[field] = true;
        } else {
          delete err?.[field];
        }
      }
    });

    if (formData.dob) {
      const age = moment().diff(moment(formData.dob), "years");
      if (age < 21) {
        err.dob = "You must be at least 21 years old";
      }
    }

    setErrors(err);
    if (!Object.keys(err)?.length) {
      persistedFormData = null;
      persistedFiles = null;

      dispatch(createBackgroundReq(formData))
        .unwrap()
        .then(() => {
          handleChange("profile");
        })
        .catch(() => { });
    }
  };

  useEffect(() => {
    dispatch(getUpdatedDetailsReq());
    // eslint-disable-next-line
  }, []);

  const getDefaultDob = () => {
    if (persistedFormData?.dob) {
      return persistedFormData.dob;
    }
    return specialist?.background?.dob
      ? moment(specialist?.background?.dob).format("YYYY-MM-DD")
      : "";
  };

  const getDefaultCheckbox = (field) => {
    if (persistedFormData && persistedFormData[field] !== undefined) {
      return persistedFormData[field];
    }
    return Boolean(specialist?.background);
  };

  return (
    <Box
      sx={{
        p: 3,
        border: "1px solid #3498db",
        borderTop: "none",
        borderRadius: "0 0 6px 6px",
      }}
    >
      <Typography
        mb={2}
        fontSize={36}
        component="h2"
        fontWeight="700"
        color="other.color1"
      >
        Ensuring Trust and Safety
      </Typography>
      <Typography
        mb={1.7}
        fontSize={24}
        component="h4"
        fontWeight="600"
        color="other.color1"
      >
        Complete Your Background Check
      </Typography>
      <Typography mb={2} fontWeight="500" color="other.color2">
        To maintain the highest standards of trust and safety on our platform,
        we require all specialists to complete a background check. Please
        provide the necessary documents and information below. Rest assured,
        your personal information is handled with the utmost confidentiality and
        security
      </Typography>
      <Stack spacing={2} component="form" ref={formRef} onSubmit={handleSubmit}>
        <Box>
          <InputField1
            type="text"
            name="fullName"
            label="Full Name *"
            placeholder="Full Name"
            value={specialist?.specialist?.fullName}
            disabled
          />
          {errors?.fullName && (
            <Typography mt={1} color={red["500"]}>
              Please enter full name
            </Typography>
          )}
        </Box>
        <Box>
          <InputField1
            name="dob"
            type="date"
            label="Date of Birth *"
            placeholder="DD/MM"
            endIcon={
              <Icon sx={{ fontSize: "18px" }}>
                <Icons.CalendarSolid />
              </Icon>
            }
            slotProps={{ inputProps: { max: moment().subtract(21, "years").format("YYYY-MM-DD") } }}
            defaultValue={getDefaultDob()}
            key={getDefaultDob()}
          />
          {errors?.dob && (
            <Typography mt={1} color={red["500"]}>
              {typeof errors.dob === "string" ? errors.dob : "Please enter Date of Birth"}
            </Typography>
          )}
        </Box>
        <FileInput
          files={files}
          errors={errors}
          name="personalIdentification"
          label="Personal Identification *"
          errText="Please Upload Personal Identification"
          defaultValue={
            files.current.personalIdentification?.length
              ? files.current.personalIdentification
              : specialist?.background?.personalIdentification || []
          }
          key={`personal-${uid(6)}`}
          placeholderText="Upload Personal Identification (e.g., Passport, Driver's License)"
        />
        <FileInput
          files={files}
          errors={errors}
          name="professionalIdentification"
          label="Professional Identification *"
          errText="Please Upload Professional Identification"
          defaultValue={
            files.current.professionalIdentification?.length
              ? files.current.professionalIdentification
              : specialist?.background?.professionalIdentification || []
          }
          key={`professional-${uid()}`}
          placeholderText="Upload Professional Identification (e.g., Passport, Driver's License)"
        />
        <Box>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "600",
            }}
          >
            Background Check Consent *
          </Typography>
          <FormControlLabel
            label="I consent to a background check"
            control={
              <Checkbox
                name="backgroundCheck"
                defaultChecked={getDefaultCheckbox("backgroundCheck")}
                key={`bg-${getDefaultCheckbox("backgroundCheck")}`}
              />
            }
          />
          {errors?.backgroundCheck && (
            <Typography mt={1} color={red["500"]}>
              Please accept the consent policy
            </Typography>
          )}
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "600",
            }}
          >
            Privacy Policy Agreement *
          </Typography>
          <FormControlLabel
            label="I agree to the Privacy Policy"
            control={
              <Checkbox
                name="privacyPolicyCheck"
                defaultChecked={getDefaultCheckbox("privacyPolicyCheck")}
                key={`pp-${getDefaultCheckbox("privacyPolicyCheck")}`}
              />
            }
          />
          {errors?.privacyPolicyCheck && (
            <Typography mt={1} color={red["500"]}>
              Please accept agreement policy
            </Typography>
          )}
        </Box>
        <Box>
          <Typography
            mb={0.5}
            fontSize={24}
            component="h4"
            fontWeight="500"
            color="other.color1"
          >
            Security Assurance:
          </Typography>
          <Typography mb={2} fontWeight="800" color="other.color2">
            Your information will be securely stored and used only for
            verification purposes. We adhere to the highest standards of data
            protection and privacy.
          </Typography>
          <Typography
            mb={0.5}
            fontSize={24}
            component="h4"
            fontWeight="500"
            color="other.color1"
          >
            Need help?
          </Typography>
          <Typography mb={2} fontWeight="500" color="other.color2">
            If you have any questions or experience difficulties uploading your
            documents, please contact our support team at &nbsp;
            <Link
              underline="hover"
              component={NavLink}
              sx={{ color: "other.color1" }}
              to="mailto:support@innerpal.com"
            >
              support@innerpal.com
            </Link>
          </Typography>
          <Typography
            mb={0.5}
            fontSize={24}
            component="h4"
            fontWeight="500"
            color="other.color1"
          >
            Help Section
          </Typography>
          <Button
            sx={{
              py: 1.2,
              minWidth: "110px",
              justifyContent: "center",
            }}
            type="button"
            to="/help-center"
            variant="contained"
            LinkComponent={NavLink}
          >
            Contact Us
          </Button>
        </Box>
        <Stack spacing={2} direction="row">
          <Button
            sx={{
              py: 1.2,
              minWidth: "110px",
              justifyContent: "center",
            }}
            type="button"
            variant="contained"
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            sx={{
              py: 1.2,
              minWidth: "110px",
              justifyContent: "center",
            }}
            type="submit"
            variant="contained"
          >
            Save as Draft
          </Button>
          <Button
            sx={{
              py: 1.2,
              minWidth: "110px",
              justifyContent: "center",
            }}
            type="submit"
            variant="contained"
          >
            Next
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export default Details;
